import { getCurrentScope, onScopeDispose, reactive, toRefs } from 'vue'

/**
 * Shared reactive window size state.
 */
const sizes = reactive({
  browserWidth: window.innerWidth,
  deviceWidth: screen.width,
  isMobile: false,
  size: 'xs'
})

/**
 * Tracks whether the resize listener is currently attached.
 */
let isTrackingWindowSize = false

/**
 * Number of active composable consumers using the shared tracker.
 */
let activeTrackingConsumers = 0

/**
 * Returns the current responsive size label for the window width.
 */
const screenSizeString = (): string => {
  let string = ''
  if (window.innerWidth <= 640) {
    string = 'xs'
  } else if (window.innerWidth > 640 && window.innerWidth <= 768) {
    string = 'sm'
  } else if (window.innerWidth > 768 && window.innerWidth <= 1024) {
    string = 'md'
  } else if (window.innerWidth > 1024 && window.innerWidth <= 1280) {
    string = 'lg'
  } else if (window.innerWidth > 1280) {
    string = 'xl'
  }
  return string
}

/**
 * Returns whether the current window width should be treated as mobile.
 */
const isMobile = (): boolean => {
  return window.innerWidth <= 640
}

/**
 * Synchronizes shared size state with the latest browser values.
 */
function updateSizes(): void {
  sizes.browserWidth = window.innerWidth
  sizes.deviceWidth = screen.width
  sizes.isMobile = isMobile()
  sizes.size = screenSizeString()
}

/**
 * Starts listening for resize events when the first consumer subscribes.
 */
function startWindowSizeTracking(): void {
  if (isTrackingWindowSize) {
    return
  }

  updateSizes()
  window.addEventListener('resize', updateSizes)
  isTrackingWindowSize = true
}

/**
 * Stops listening for resize events when the last consumer unsubscribes.
 */
function stopWindowSizeTracking(): void {
  if (!isTrackingWindowSize) {
    return
  }

  window.removeEventListener('resize', updateSizes)
  isTrackingWindowSize = false
}

/**
 * Registers the current composable consumer with the shared resize tracker.
 */
function registerWindowSizeTracking(): void {
  activeTrackingConsumers += 1

  if (activeTrackingConsumers === 1) {
    startWindowSizeTracking()
  }

  if (!getCurrentScope()) {
    return
  }

  onScopeDispose(() => {
    activeTrackingConsumers = Math.max(0, activeTrackingConsumers - 1)

    if (activeTrackingConsumers === 0) {
      stopWindowSizeTracking()
    }
  })
}

/**
 * Provides reactive window size values for the current component scope.
 */
export function useWindowSize() {
  registerWindowSizeTracking()

  return {
    ...toRefs(sizes)
  }
}

/**
 * Returns the active breakpoint value based on the shared window size state.
 * @param breakpoints - Breakpoint map keyed by size label
 */
export function useBreakPoints(breakpoints: Record<string, number> = {}) {
  registerWindowSizeTracking()

  // Example breakpoint obj: { xs: 'horizontal', sm: 'vertical', md: 'vertical', lg: 'horizontal' }

  // Check that the passed argument is an object
  if (typeof breakpoints !== 'object') {
    throw new Error('The argument in useBreakPoints must be an object')
  }

  // Check that the key 'xs' exists in the object
  if (!breakpoints.xs) {
    throw new Error('The argument in useBreakPoints must contain a key of "xs" as the base break point')
  }

  const screenSizeMapping = {
    xs: 640,
    sm: 768,
    md: 1024,
    lg: 1280,
    xl: 1920
  }

  // Loop through the mapping, create a new object with the same keys, but with the values from the breakpoints object
  // Keep track of latest breakpoint value
  let latestBreakpointKey: string | null = null
  for (const key in screenSizeMapping) {
    // If the key doesn't exist in the breakpoints object, use the latest breakpoint value
    if (breakpoints[key]) {
      screenSizeMapping[key as keyof typeof screenSizeMapping] = breakpoints[key]
      latestBreakpointKey = key
    } else {
      screenSizeMapping[key as keyof typeof screenSizeMapping] =
        screenSizeMapping[latestBreakpointKey as keyof typeof screenSizeMapping]
    }
  }

  // Example mapping: { "xs": "horizontal", "sm": "horizontal", "md": "vertical", "lg": "horizontal", "xl": "horizontal" }
  return screenSizeMapping[sizes.size as keyof typeof screenSizeMapping]
}
